import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HeartPulse } from 'lucide-react';
import type { HealthStatusDto } from '../models/types';

// Fluent Badge color name -> shadcn Badge class, preserving status color SEMANTICS.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};
function badgeClass(c: string): string {
  return BADGE_CLASS[c] || BADGE_CLASS.informative;
}

interface HealthStatusCardProps {
  healthStatus: HealthStatusDto[];
}

export const HealthStatusCard: React.FC<HealthStatusCardProps> = ({ healthStatus = [] }) => {
  // API may return status as int enum or string
  const statusToString = (s: any): string => {
    if (typeof s === 'string') return s;
    // Map common enum values: 0=Unknown, 1=Healthy, 2=Degraded, 3=Unhealthy
    const map: Record<number, string> = { 0: 'Unknown', 1: 'Healthy', 2: 'Degraded', 3: 'Unhealthy' };
    return map[s] ?? `Status ${s}`;
  };

  const getStatusBadge = (status: any) => {
    const str = statusToString(status);
    const color = str.toLowerCase() === 'healthy' ? 'success' : str.toLowerCase() === 'degraded' ? 'warning' : 'danger';
    return <Badge variant="outline" className={badgeClass(color)}>{str}</Badge>;
  };

  // Determine overall status (worst status across all services)
  const overallStatus = healthStatus.length === 0
    ? 'Unknown'
    : healthStatus.some(s => statusToString(s.status).toLowerCase() === 'unhealthy')
      ? 'Unhealthy'
      : healthStatus.some(s => statusToString(s.status).toLowerCase() === 'degraded')
        ? 'Degraded'
        : 'Healthy';

  const lastCheck = healthStatus.length > 0
    ? new Date(healthStatus[0].lastCheckedAt).toLocaleString()
    : 'Never';

  return (
    <Card>
      <div className="flex items-center gap-3 p-4 pb-0">
        <HeartPulse className="h-6 w-6 text-accent-text" />
        <div>
          <p className="text-sm font-semibold text-foreground">System Health</p>
          <span className="text-xs text-muted-foreground">Last check: {lastCheck}</span>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <p className="text-sm">Overall Status:</p>
            {getStatusBadge(overallStatus)}
          </div>
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          {healthStatus.map((service) => (
            <div
              key={service.serviceName}
              className="border-b border-border"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
              }}
            >
              <div>
                <span className="block text-xs text-muted-foreground">{service.serviceName}</span>
                {service.message && (
                  <span className="block text-xs text-muted-foreground">
                    {service.message}
                  </span>
                )}
              </div>
              {getStatusBadge(service.status)}
            </div>
          ))}
        </div>

        {healthStatus.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <span className="text-xs text-muted-foreground">No health data available</span>
          </div>
        )}
      </div>
    </Card>
  );
};
